/** @jsx jsx */
import { Alert, Button, ButtonGroup, Intent, Menu, MenuItem, Popover, Position } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { css, jsx } from '@emotion/core';
import { Fragment, useState } from 'react';
import AppToaster from '../../../AppToaster';
import {
  CompanionInfoFragment,
  useActivateCompanionMutation,
  useApproveCompanionMutation,
  useDeactivateCompanionMutation,
  useDeleteCompanionMutation,
  useDenyCompanionMutation,
  useForceDeleteCompanionMutation,
  useRestoreCompanionMutation,
  UserStatus,
} from '../../../generated/graphql';

type CompanionDetailMenuProps = {
  companion: CompanionInfoFragment;
  onEdit: () => void;
};

const CompanionDetailMenu = ({ companion, onEdit }: CompanionDetailMenuProps) => {
  const { user, id } = companion;
  const options = {
    variables: {
      id,
    },
  };
  const [isForceDeleteAlertOpen, setIsForceDeleteAlertOpen] = useState(false);
  const [approveCompanion, { loading: approving }] = useApproveCompanionMutation({
    ...options,
    onCompleted: () => AppToaster.success(`${user.display_name}  wurde freigeschaltet`),
  });
  const [denyCompanion, { loading: denying }] = useDenyCompanionMutation({
    ...options,
    onCompleted: () => AppToaster.success(`${user.display_name}  wurde abgelehnt`),
  });
  const [activateCompanion, { loading: activating }] = useActivateCompanionMutation({
    ...options,
    onCompleted: () => AppToaster.success(`${user.display_name}  wurde ACTIV geschaltet`),
  });
  const [deactivateCompanion, { loading: deactivating }] = useDeactivateCompanionMutation({
    ...options,
    onCompleted: () => AppToaster.success(`${user.display_name}  wurde PASSIV geschaltet`),
  });
  const [deleteCompanion, { loading: deleting }] = useDeleteCompanionMutation({
    ...options,
    onCompleted: () => AppToaster.success(`${user.display_name}  wurde in den Papierkorb gelegt`),
  });
  const [restoreCompanion, { loading: restoring }] = useRestoreCompanionMutation({
    ...options,
    onCompleted: () => AppToaster.success(`${user.display_name}  wurde wiederhergestellt`),
  });
  const [forceDeleteCompanion, { loading: forceDeleting }] = useForceDeleteCompanionMutation({
    ...options,
    onCompleted: () => AppToaster.success(`${user.display_name}  wurde endgültig entfernt`),
  });

  if (!!companion.deleted_at) {
    return (
      <Fragment>
        <Alert
          isOpen={isForceDeleteAlertOpen}
          cancelButtonText="Abbrechen"
          confirmButtonText="Entfernen"
          icon={IconNames.DELETE}
          intent={Intent.DANGER}
          onClose={() => setIsForceDeleteAlertOpen(false)}
          onConfirm={forceDeleteCompanion as any}
        >
          <p>Soll dieser Begleiter wirklich undwiderruflich entfernt werden?</p>
        </Alert>
        <Button
          text="Wiederherstellen"
          icon={IconNames.OFFLINE}
          css={styles.headerButton}
          onClick={restoreCompanion as any}
          loading={restoring}
        />
        <Popover
          position={Position.BOTTOM}
          content={
            <Menu>
              <MenuItem text="Bearbeiten" icon={IconNames.EDIT} onClick={onEdit} />
              <MenuItem
                text="Entfernen"
                icon={IconNames.DELETE}
                intent={Intent.DANGER}
                onClick={() => setIsForceDeleteAlertOpen(true)}
              />
            </Menu>
          }
        >
          <Button icon={IconNames.MORE} css={styles.headerButton} loading={forceDeleting} />
        </Popover>
      </Fragment>
    );
  }

  if (user.status === UserStatus.New) {
    return (
      <Fragment>
        <ButtonGroup css={styles.headerButton}>
          <Button text="Bestätigen" icon={IconNames.TICK} onClick={approveCompanion as any} loading={approving} />
          <Button text="Ablehnen" icon={IconNames.CROSS} onClick={denyCompanion as any} loading={denying} />
        </ButtonGroup>
        <Popover
          position={Position.BOTTOM}
          content={
            <Menu>
              <MenuItem text="Bearbeiten" icon={IconNames.EDIT} onClick={onEdit} />
              <MenuItem text="In den Papierkorb" icon={IconNames.TRASH} onClick={deleteCompanion as any} />
            </Menu>
          }
        >
          <Button icon={IconNames.MORE} css={styles.headerButton} />
        </Popover>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Button text="Bearbeiten" icon={IconNames.EDIT} css={styles.headerButton} onClick={onEdit} />
      <Popover
        position={Position.BOTTOM}
        content={
          <Menu>
            {user.status === UserStatus.Active ? (
              <MenuItem text="Passiv schalten" icon={IconNames.OFFLINE} onClick={deactivateCompanion as any} />
            ) : (
              <MenuItem text="Aktiv schalten" icon={IconNames.OFFLINE} onClick={activateCompanion as any} />
            )}
            <MenuItem text="In den Papierkorb" icon={IconNames.TRASH} onClick={deleteCompanion as any} />
          </Menu>
        }
      >
        <Button icon={IconNames.MORE} css={styles.headerButton} loading={activating || deactivating || deleting} />
      </Popover>
    </Fragment>
  );
};

export default CompanionDetailMenu;

const styles = {
  statusTag: css`
    margin-left: 10px;
  `,
  headerButton: css`
    margin-left: 10px;
  `,
};
